/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type PropsWithChildren, type RefObject, useContext } from 'react';

import { css, jsx } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { useAppContext } from '../../../app-context';
import { PinnedRightPanelContext } from '../../../layout/right-panel-context';

const styles = css({
	display: 'flex',
	boxSizing: 'border-box',
	flexDirection: 'column',
	overflowY: 'auto',
	overflowX: 'hidden',
	paddingBlock: token('space.250'),
	paddingInline: token('space.050'),
	justifyContent: 'start',
});

const fillContainerStyles = cssMap({
	container: {
		overflowX: 'visible',
		overflowY: 'visible',
	},
	pinned: {
		minHeight: 0,
		maxHeight: '100%',
		overflowX: 'hidden',
		overflowY: 'auto',
	},
});

export type AppFilterSidebarContainerProps = {
	ariaLabel: string;
	testId?: string;
	/** Let the enclosing FPS layout own scrolling and the sidebar size naturally. */
	fillContainer?: boolean;
	/**
	 * Optional ref to the underlying container element. Used by analytics
	 * fire sites (e.g. `viewed searchPageFilters` from the non-lite RightPanel)
	 * to DOM-measure which app filter rows are currently pixel-visible.
	 */
	containerRef?: RefObject<HTMLDivElement>;
};

// TODO: QS-8171 sidebar height calculation should be improved to not re-define nav/header heights
const NAV3_HEIGHT = 56;
const NAV4_HEIGHT = 48;
const HEADER_FILTER_HEIGHT_WITH_NAV3 = 140;
const HEADER_FILTER_HEIGHT_WITH_NAV4 = 76;

export const SidebarContainer: ({
	ariaLabel,
	testId,
	children,
	containerRef,
	fillContainer,
}: PropsWithChildren<AppFilterSidebarContainerProps>) => JSX.Element = ({
	ariaLabel,
	testId,
	children,
	containerRef,
	fillContainer = false,
}: PropsWithChildren<AppFilterSidebarContainerProps>) => {
	const { isNav4Enabled } = useAppContext();
	const rightPanelTarget = useContext(PinnedRightPanelContext);
	const navHeight = isNav4Enabled ? NAV4_HEIGHT : NAV3_HEIGHT;
	const headerFilterHeight = isNav4Enabled
		? HEADER_FILTER_HEIGHT_WITH_NAV4
		: HEADER_FILTER_HEIGHT_WITH_NAV3;
	return (
		<div
			ref={containerRef}
			role="group"
			aria-label={ariaLabel}
			data-testid={testId}
			css={[
				styles,
				fillContainer && fillContainerStyles.container,
				fillContainer && rightPanelTarget && fillContainerStyles.pinned,
			]}
			// Gate cleanup must preserve legacy sizing until the remaining hosts adopt fillContainer.
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Legacy height depends on runtime navigation; container mode omits it.
			style={
				fg('rovo_fps_fix_duplicate_scrollbars') && fillContainer
					? undefined
					: { height: `calc(100vh - ${navHeight}px - ${headerFilterHeight}px)` }
			}
		>
			{children}
		</div>
	);
};
